// projectForm — the RHF/Zod form shapes + wire mappers for the platform-projects
// create wizard and edit drawers (ADR-008). These form schemas are wizard/drawer-
// LOCAL (the RoleCreatePage pattern): validation is never stricter than the backend
// (which re-validates the request schemas in packages/schemas), it just gates
// Next/Submit earlier. onSubmit maps the form values → the wire request DTO.

import { z } from 'zod';
import { LanguageSchema } from '@ems-app/schemas/common/language';
import { isIanaZone } from '@ems-app/schemas/common/zone';
import { TrialLengthSchema, type TrialLength } from '@ems-app/schemas/projects/platform-requests';
import type { CreatePlatformProjectRequest, ProjectTierInput } from '@ems-app/schemas/projects/platform-requests';
import type { LicenseTier } from '@ems-app/schemas/projects/platform-responses';

const HEX = /^#[0-9a-fA-F]{6}$/;

export type { TrialLength };
export const TRIAL_LENGTHS: readonly TrialLength[] = TrialLengthSchema.options;

// Project code = a short unique handle derived from the name (uppercase, hyphenated)
// — the name→slug pattern (Vercel/GitHub). Editable in the wizard, immutable after.
export function slugifyCode(name: string): string {
  return name
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32);
}

// now + a trial length → an absolute ISO instant (the trial-extend dialog sends an
// absolute date; create sends the duration and the backend stamps it). UTC math so
// it's timezone-stable (mirrors the backend's trialExpiryFromLength).
export function trialExpiryISO(length: TrialLength, now: Date = new Date()): string {
  const d = new Date(now);
  if (length === '1w') d.setUTCDate(d.getUTCDate() + 7);
  else if (length === '1m') d.setUTCMonth(d.getUTCMonth() + 1);
  else if (length === '3m') d.setUTCMonth(d.getUTCMonth() + 3);
  else if (length === '6m') d.setUTCMonth(d.getUTCMonth() + 6);
  return d.toISOString();
}

// ── tier editor model ─────────────────────────────────────────────────────────
// One catalogue tier as the editor sees it: whether this project INCLUDES it (every
// type — ADR-055) + the seats a prepaid project buys for it. Seeded from the
// catalogue; for edit, merged with the project's included tiers (a tier not
// included is present-but-off).
export interface TierDraft {
  code: string;
  included: boolean;
  seats: number;
}

export function seedTiers(
  catalogue: readonly LicenseTier[],
  included?: readonly { tier_code: string; seats: number | null }[],
): TierDraft[] {
  return catalogue.map((cat) => {
    const held = included?.find((s) => s.tier_code === cat.code);
    // Create (no `included`): default every tier included. Edit: included iff the project holds it.
    return { code: cat.code, included: included ? !!held : true, seats: held?.seats ?? 0 };
  });
}

// tiers → the wire tiers[]: every type sends the tiers it includes — a subscription (esco) without seats (metered),
// a prepaid project with the seats it buys.
export function toTierInputs(tiers: readonly TierDraft[], type: 'esco' | 'embedded' | 'demo'): ProjectTierInput[] {
  return tiers.filter((t) => t.included).map((t) => ({ tier_code: t.code, seats: type === 'esco' ? null : t.seats }));
}

// ── the create wizard form schema ──────────────────────────────────────────────
const tierDraftSchema = z.object({ code: z.string(), included: z.boolean(), seats: z.number().int().min(0) });

export const createProjectFormSchema = z.object({
  display_name: z.string().trim().min(1).max(64),
  name: z.string().trim().min(1).max(32),
  type: z.enum(['esco', 'embedded', 'demo']),
  description: z.string().trim().max(1000),
  color_primary: z.string().regex(HEX),
  color_on_primary: z.string().regex(HEX),
  mother_company: z.string().trim().max(80),
  service_provider: z.string().trim().max(80),
  service_provider_address: z.string().trim().max(300),
  service_provider_support_email: z.union([z.string().trim().email(), z.literal('')]),
  countries: z.array(z.string().length(2)).min(1),
  default_country_iso_code: z.string().length(2).nullable(),
  // The project's zone and week start (ADR-082 §3) — pre-filled from the default country (else the first listed), the
  // sysadmin's to override; the zone judged by the one zone check, as the wire's.
  timezone_iana: z.string().min(1).max(64).refine(isIanaZone, 'INVALID_TIMEZONE'),
  week_start: z.number().int().min(1).max(7),
  // The base currency — pre-filled from the default country's usual one, the sysadmin's to override; a code the runtime knows (the server's test).
  default_currency: z.string().length(3),
  default_language: LanguageSchema,
  has_invoicing: z.boolean(),
  has_field_force: z.boolean(),
  has_vehicles: z.boolean(),
  tiers: z.array(tierDraftSchema),
  is_trial: z.boolean(),
  trial_length: TrialLengthSchema,
});
export type CreateProjectFormValues = z.infer<typeof createProjectFormSchema>;

// Fields each wizard step owns — Next validates only these (RoleCreatePage pattern).
export const CREATE_STEP_FIELDS: (keyof CreateProjectFormValues)[][] = [
  [
    'display_name',
    'name',
    'description',
    'mother_company',
    'service_provider',
    'service_provider_address',
    'service_provider_support_email',
    'color_primary',
    'color_on_primary',
  ],
  ['countries', 'default_country_iso_code', 'timezone_iana', 'week_start', 'default_currency'],
  [],
];

export function toCreateRequest(v: CreateProjectFormValues): CreatePlatformProjectRequest {
  return {
    name: v.name,
    display_name: v.display_name,
    type: v.type,
    description: v.description,
    color_primary: v.color_primary,
    color_on_primary: v.color_on_primary,
    mother_company: v.mother_company,
    service_provider: v.service_provider,
    service_provider_address: v.service_provider_address,
    service_provider_support_email: v.service_provider_support_email,
    countries: v.countries,
    default_country_iso_code: v.default_country_iso_code,
    timezone_iana: v.timezone_iana,
    week_start: v.week_start,
    default_currency: v.default_currency,
    default_language: v.default_language,
    has_invoicing: v.has_invoicing,
    has_field_force: v.has_field_force,
    has_vehicles: v.has_vehicles,
    tiers: toTierInputs(v.tiers, v.type),
    is_trial: v.is_trial,
    trial_length: v.is_trial ? v.trial_length : null,
  };
}

// ── edit-drawer form schemas ────────────────────────────────────────────────────
// Sysadmin identity edit: display name · mother company · description (code + type
// are immutable after create).
export const identityFormSchema = z.object({
  display_name: z.string().trim().min(1).max(64),
  mother_company: z.string().trim().max(80),
  service_provider: z.string().trim().max(80),
  service_provider_address: z.string().trim().max(300),
  service_provider_support_email: z.union([z.string().trim().email(), z.literal('')]),
  description: z.string().trim().max(1000),
});
export type IdentityFormValues = z.infer<typeof identityFormSchema>;

export const brandFormSchema = z.object({
  color_primary: z.string().regex(HEX),
  color_on_primary: z.string().regex(HEX),
});
export type BrandFormValues = z.infer<typeof brandFormSchema>;

export const countriesFormSchema = z.object({
  countries: z.array(z.string().length(2)).min(1),
});
export type CountriesFormValues = z.infer<typeof countriesFormSchema>;

export const licensingFormSchema = z.object({
  has_invoicing: z.boolean(),
  has_field_force: z.boolean(),
  has_vehicles: z.boolean(),
  tiers: z.array(tierDraftSchema),
});
export type LicensingFormValues = z.infer<typeof licensingFormSchema>;

// The trial-extend form (the "convert to full" action fires a separate mutation).
export const trialFormSchema = z.object({ trial_length: TrialLengthSchema });
export type TrialFormValues = z.infer<typeof trialFormSchema>;

// License-tier add/rename form (the manage-tiers Modal). Add uses all fields; rename
// edits `name` only (code + cap immutable, shown read-only). Empty cap = unbounded.
export const licenseTierFormSchema = z.object({
  code: z.string().trim().min(1).max(16).regex(/^[a-z][a-z0-9_]*$/, 'TIER_CODE_FORMAT'),
  name: z.string().trim().min(1).max(32),
  max_active_power_systems: z.number().int().min(1).nullable(),
});
export type LicenseTierFormValues = z.infer<typeof licenseTierFormSchema>;
